import { defineMessages, useIntl } from 'react-intl';
import { useMcpContext } from './McpContextProvider';

const atlassianMessages = defineMessages({
  brand: {
    id: 'oauth.mcp.brand.atlassian',
    defaultMessage: 'Atlassian',
    description:
      'Brand name in MCP consent copy when Atlassian product scopes are present',
  },
  productUnit: {
    id: 'oauth.mcp.brand.productUnit.atlassian',
    defaultMessage: 'site',
    description:
      'Organizational unit name for Atlassian (Atlassian / mixed flows)',
  },
});

const trelloMessages = defineMessages({
  brand: {
    id: 'oauth.mcp.brand.trello',
    defaultMessage: 'Trello',
    description:
      'Brand name in MCP consent copy when only Trello scopes are present',
  },
  productUnit: {
    id: 'oauth.mcp.brand.productUnit.trello',
    defaultMessage: 'Trello workspace',
    description: 'Organizational unit name for Trello (Trello-only flow)',
  },
});

export interface McpTerminology {
  /* Product name */
  product: string;
  /* Product unit e.g. site for tenanted products and workspace for Trello */
  productUnit: string;
}

/**
 * Returns the Atlassian brand name, regardless of scope classification.
 *
 * Used for copy that always refers to Atlassian (e.g. the legal disclaimer,
 * whose privacy policy and terms of use links always point to Atlassian).
 */
export function useAtlassianBrand(): string {
  const { formatMessage } = useIntl();
  return formatMessage(atlassianMessages.brand);
}

/**
 * Returns product-specific terminology based on scope classification.
 */
export function useMcpBranding(): McpTerminology {
  const { formatMessage } = useIntl();
  const { scopeClassification } = useMcpContext();
  const isTrelloOnly =
    scopeClassification.hasTrello && !scopeClassification.hasAtlassian;

  const m = isTrelloOnly ? trelloMessages : atlassianMessages;
  return {
    product: formatMessage(m.brand),
    productUnit: formatMessage(m.productUnit),
  };
}
